/**
 * Centralized type definitions and configuration for word overlays and block types
 */

// === Block Type Definitions ===
export type BlockType = 
  | ''
  | 'book-name'
  | 'chapter'
  | 'character-name'
  | 'content'
  | 'image-caption'
  | 'image-description'
  | 'information-sign'
  | 'object-text'
  | 'page-number'
  | 'q-and-a-answer'
  | 'q-and-a-question'
  | 'sound-effect'
  | 'speech-bubble'
  | 'title';

export const BLOCK_TYPES: BlockType[] = [
  'book-name',
  'chapter',
  'title',
  'content',
  'character-name',
  'image-caption',
  'image-description',
  'information-sign',
  'object-text',
  'q-and-a-answer',
  'q-and-a-question',
  'sound-effect',
  'speech-bubble',
  'page-number'
];

// === Word Data Interface ===
export interface WordData {
  wordId: string | number;
  text: string;
  boundingBox: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  confidence?: number;
  originalBoundingBox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

// === Block Data Interface ===
export interface BlockData {
  blockId: string | number;
  blockType: BlockType;
  blockReadOrder: number;
  boundingBox: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  words: WordData[];
  readBlock?: boolean;
  'ai-confidence'?: number;
  possibleIssue?: string;
  originalBlockData?: any;
  originalProcessedData?: any;
  originalTextractData?: any;
  mergedFromBlocks?: (string | number)[]; // Track IDs of blocks that were merged into this block
}

// === Block Type Color Configuration ===
export const BLOCK_TYPE_COLORS: Record<BlockType, string> = {
  '': 'rgba(200, 200, 200, 0.5)',              // Light grey with transparency for "No Block Type"
  'book-name': 'rgba(200, 150, 255, 0.9)',     // Light purple-blue
  'chapter': 'rgba(150, 200, 255, 0.9)',      // Medium blue
  'character-name': 'rgba(255, 200, 100, 0.9)', // Light orange
  'content': 'rgba(150, 255, 150, 0.9)',      // Medium green
  'image-caption': 'rgba(200, 200, 255, 0.9)',  // Light blue
  'image-description': 'rgba(200, 200, 255, 0.9)',  // Light blue
  'information-sign': 'rgba(200, 200, 200, 0.9)', // Light grey
  'object-text': 'rgba(150, 255, 150, 0.9)',  // Medium green
  'page-number': 'rgba(255, 200, 200, 0.9)',  // Light red
  'q-and-a-answer': 'rgba(220, 255, 220, 0.9)',  // Light green
  'q-and-a-question': 'rgba(255, 220, 150, 0.9)', // Light yellow-orange
  'sound-effect': 'rgba(255, 100, 100, 0.9)',   // Light red
  'speech-bubble': 'rgba(255, 150, 255, 0.9)', // Light purple
  'title': 'rgba(100, 150, 255, 0.9)'        // Deep blue
};

// === Word Confidence Color Configuration ===
export interface WordConfidenceColor {
  min: number;
  color: string;
}

export const WORD_CONFIDENCE_COLORS: WordConfidenceColor[] = [
  { min: 98, color: 'rgba(2, 136, 209, 0.3)' },  
  { min: 95, color: 'rgba(179, 229, 252, 0.3)' },
  { min: 90, color: 'rgba(200, 230, 201, 0.3)' },
  { min: 80, color: 'rgba(255, 245, 157, 0.3)' },
  { min: 0,  color: 'rgba(255, 178, 157, 0.3)' } 
];

// === Utility Functions ===

/**
 * Get block border color based on readBlock status
 * Green if readBlock is true, red if false
 */
export function getBlockColor(readBlock: boolean | undefined): string {
  if (readBlock === true) {
    return 'green';
  }
  return 'red';
}

/**
 * Get word background color based on confidence
 */
export function getWordBackgroundColor(confidence: number): string {
  for (const colorConfig of WORD_CONFIDENCE_COLORS) {
    if (confidence >= colorConfig.min) {
      return colorConfig.color;
    }
  }
  return WORD_CONFIDENCE_COLORS[WORD_CONFIDENCE_COLORS.length - 1].color;
}

/**
 * Validate block type
 */
export function isValidBlockType(type: string): type is BlockType {
  return Object.keys(BLOCK_TYPE_COLORS).includes(type);
}

/**
 * Normalize block type to ensure type safety
 */
export function normalizeBlockType(type: string): BlockType {
  if (isValidBlockType(type)) {
    return type;
  }
  
  // Map common variations to standard types
  const typeMapping: Record<string, BlockType> = {
    'text': 'content',
    'line': 'content',
    'word': 'content',
    'paragraph': 'content',
    'heading': 'title',
    'caption': 'image-caption',
    'speech': 'speech-bubble',
    'character': 'character-name',
    'sound': 'sound-effect',
    'effect': 'sound-effect',
    'page': 'page-number',
    'number': 'page-number',
    'info': 'information-sign',
    'object': 'object-text',
    'description': 'image-description',
    'question': 'q-and-a-question',
    'answer': 'q-and-a-answer',
    'q-and-a': 'q-and-a-question',
    'ch': 'chapter',
    'book': 'book-name',
    'name': 'book-name'
  };
  
  const normalizedType = typeMapping[type.toLowerCase()];
  return normalizedType || 'content';
}

/**
 * Calculate percentage position for overlay elements
 */
export function calculatePercentagePosition(
  elementBox: { x: number; y: number; width: number; height: number },
  containerBox: { width: number; height: number }
): {
  left: number;
  top: number;
  width: number;
  height: number;
} {
  return {
    left: (elementBox.x / containerBox.width) * 100,
    top: (elementBox.y / containerBox.height) * 100,
    width: (elementBox.width / containerBox.width) * 100,
    height: (elementBox.height / containerBox.height) * 100
  };
}

/**
 * Calculate relative position within a block
 */
export function calculateRelativePosition(
  wordBox: { x: number; y: number; width: number; height: number },
  blockBox: { x: number; y: number; width: number; height: number }
): {
  left: number;
  top: number;
  width: number;
  height: number;
} {
  const relativeX = wordBox.x - blockBox.x;
  const relativeY = wordBox.y - blockBox.y;
  
  return {
    left: (relativeX / blockBox.width) * 100,
    top: (relativeY / blockBox.height) * 100,
    width: (wordBox.width / blockBox.width) * 100,
    height: (wordBox.height / blockBox.height) * 100
  };
}

// === Word Overlay Configuration ===
export const WORD_OVERLAY_CONFIG = {
  // Z-Index values
  OVERLAY_CONTAINER_Z_INDEX: 10,
  BLOCK_Z_INDEX: 20,
  BLOCK_INFO_Z_INDEX: 30,
  WORD_SPAN_Z_INDEX: 40,
  
  // Font settings
  WORD_FONT_SIZE: '12px',
  WORD_FONT_WEIGHT: 'bold',
  BLOCK_INFO_FONT_SIZE: '10px',
  BLOCK_INFO_FONT_WEIGHT: 'bold',
  
  // Visual settings
  WORD_BORDER_RADIUS: '2px',
  BLOCK_INFO_BORDER_RADIUS: '3px',
  HOVER_SCALE: 1.1,
  TRANSITION_DURATION: '0.2s',
  
  // Opacity settings
  BLOCK_INFO_OPACITY_VISIBLE: 0.9,
  BLOCK_INFO_OPACITY_HIDDEN: 0.3
};

// === CSS Classes ===
export const WORD_OVERLAY_CLASSES = {
  CONTAINER: 'word-overlay-container',
  CONTAINER_ACTIVE: 'word-overlay-container-active',
  CONTAINER_SHOW_WORDS: 'word-overlay-container-show-words',
  BLOCK: 'word-block',
  BLOCK_INFO: 'word-block-info',
  WORD_SPAN: 'word-span',
  WORD_SPAN_HIGHLIGHTED: 'word-span-highlighted'
};

export default {
  BLOCK_TYPE_COLORS,
  WORD_CONFIDENCE_COLORS,
  WORD_OVERLAY_CONFIG,
  WORD_OVERLAY_CLASSES,
  getBlockColor,
  getWordBackgroundColor,
  isValidBlockType,
  normalizeBlockType,
  calculatePercentagePosition,
  calculateRelativePosition
};